import React, { useState, useEffect, useCallback } from "react";
import {
  DrawerProps,
  Checkbox,
  TextField,
  FormControlLabel,
} from "@mui/material";
import BottomDrawer, { OnAddOrSave } from "../../common/BottomDrawer";
import { WithGrow } from "@common/components/WithGrow";
import { useTranslation } from "react-i18next";
import { SelectField } from "../../common/Controls";
import { Account, BillingCode, Type } from "@services/account/types";
import { RoomsRealtimeContext } from "@common/components/ListDetailViews/Layout/context";
import { useErrorNotification } from "@common/components/Notification";
import { getAccount } from "@services/account";
import { ElogBillingEntry } from "@services/meetings/types";

type ContentProps = {
  billingEntry: Partial<ElogBillingEntry>;
  onChange: (form: Partial<ElogBillingEntry>) => void;
};
const sx = {
  width: "40ch",
  minWidth: "25%",
};
const Content = (props: ContentProps) => {
  const { billingEntry, onChange } = props;
  const context = React.useContext(RoomsRealtimeContext);
  const dispatchError = useErrorNotification();
  const meeting = context.detail?.meeting;
  const { t } = useTranslation();

  const [billingCodes = [], setBillingCodes] = useState<BillingCode[]>();
  const [frequentOnly, setFrequentOnly] = useState<boolean>(true);
  const [selected, setSelected] = useState<BillingCode["billingCode"]>();
  const [quantity, setQuantity] = useState<string>();

  const fetchBillingCodes = useCallback(
    async (id: Account["id"]) => {
      try {
        let codes = (
          (await getAccount(id)).details?.accountBillingCodes ?? []
        ).filter(({ type, active }) => active && type === Type.PASSTHROUGH);

        const found = codes.find(
          (x) => x.billingCode === billingEntry.billingCode
        );
        if (found && found.frequent === false) setFrequentOnly(false);
        if (found === undefined) {
          codes = [
            ...codes,
            { billingCode: billingEntry.billingCode, frequent: true },
          ];
        }
        setBillingCodes(codes);
      } catch (e) {
        dispatchError(e);
      }
    },
    [billingEntry.billingCode]
  );
  useEffect(() => {
    if (meeting && meeting.account) fetchBillingCodes(meeting.account?.id);
  }, [meeting]);

  useEffect(() => {
    if (quantity !== undefined) onChange({ quantity: parseInt(quantity) });
  }, [quantity]);
  useEffect(() => {
    if (selected !== undefined) {
      const found = billingCodes.find((x) => x.billingCode === selected);
      if (found !== undefined)
        onChange({
          description: found.description,
          billingCode: found.billingCode,
        });
    }
  }, [selected]);

  const handleQuantityChange = (value: string) => {
    setQuantity((old) => {
      // Only matching positive integers, regex required as `parseInt("6string") === 6`
      const isValidNumber = !isNaN(parseInt(value)) && value.match(/^[0-9]*$/);
      return isValidNumber || value === "" ? value : old;
    });
  };
  return (
    <WithGrow
      direction="row-reverse"
      fixed={
        <FormControlLabel
          control={
            <Checkbox
              checked={frequentOnly}
              onChange={(_, checked) => setFrequentOnly(checked)}
            />
          }
          label={t("Frequent billing codes only")}
          labelPlacement="end"
          sx={{ padding: 1 }}
        />
      }
      containerSx={{
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        padding: 1,
        "& > .MuiFormControl-root": { mx: 0.5 },
      }}
    >
      {billingEntry.roomName && (
        <TextField
          label={t("Room Name")}
          value={billingEntry.roomName ?? ""}
          disabled={true}
          sx={sx}
        />
      )}
      <SelectField
        label={t("Billing Code")}
        options={billingCodes
          .filter(({ frequent, description, billingCode }) => {
            const isDefined =
              billingCode !== undefined && description !== undefined;
            return (!frequentOnly || frequent) && isDefined;
          })
          .map(({ billingCode, description }) => ({
            label: description
              ? `${billingCode} (${description})`
              : billingCode,
            value: billingCode,
          }))}
        selected={selected ?? billingEntry.billingCode ?? ""}
        onSelect={setSelected}
        sx={sx}
        required
      />
      <TextField
        label={t("Quantity")}
        value={quantity ?? billingEntry.quantity?.toString() ?? ""}
        sx={sx}
        onChange={(evt) => handleQuantityChange(evt.target.value)}
        required
        slotProps={{
          htmlInput: { inputMode: "numeric", pattern: "[0-9]*" }
        }}
      />
    </WithGrow>
  );
};

const Container = (
  props: DrawerProps & {
    billingEntry?: Partial<ElogBillingEntry>;
    onSave: (v: Partial<ElogBillingEntry>) => void;
    onClose: () => void;
  }
) => {
  const { t } = useTranslation();
  const { billingEntry, onSave, onClose, ...drawerProps } = props;
  const isEdit = billingEntry?.id !== undefined;
  const [form, setForm] = useState<Partial<ElogBillingEntry>>(
    billingEntry ?? {}
  );

  useEffect(() => {
    setForm(billingEntry ?? {});
  }, [billingEntry]);
  const isValidForm = (form: Partial<ElogBillingEntry>) => {
    const areDefined = (keys: (keyof typeof form)[]) =>
      keys.every((key) => form[key] !== undefined);
    const isQuantityOverZero = () => (form.quantity ?? 0) > 0;
    return areDefined(["billingCode", "quantity"]) && isQuantityOverZero();
  };
  const saveActionProps: OnAddOrSave = isEdit
    ? { onSave: () => onSave(form) }
    : { onAdd: () => onSave(form) };
  return (
    <BottomDrawer
      title={t(isEdit ? "Edit Billing Entry" : "Add Billing Entry")}
      disabled={!isValidForm(form)}
      onClose={onClose}
      {...saveActionProps}
      {...drawerProps}
    >
      {props.open && (
        <Content
          billingEntry={form}
          onChange={(changes) => setForm((old) => ({ ...old, ...changes }))}
        />
      )}
    </BottomDrawer>
  );
};

export default Container;
